import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { capitalize } from 'lodash';

import { FamilyMemberCard } from '../FamilyMemberCard';
import {
  GET_DEPENDENTS_QUERY_KEY,
  RESTORE_DEPENDENT_QUERY_KEY,
} from 'enrollment/queries/useEnrollmentFamilyQuery';
import { ErrorResponseDto, ResponseDto } from 'theSource/types';
import { useApiMutation } from 'theSource/utils';
import { useConfigurationQuery } from 'theSource/queries';
import { Loader } from 'theSource/components';
import { PSModalWrapper } from 'enrollment/components/Shared/Modal';

export interface RemovedFamilyMemberProps {
  enrollmentContextType?: string | null;
  data?: object[];
  dependentsData?: any;
}

export const RemovedFamilyMember = ({
  enrollmentContextType,
  data,
  dependentsData,
}: RemovedFamilyMemberProps) => {
  const { t } = useTranslation('pages.enrollmentFamily');

  const [dependentId, setDependentId] = useState<string>('');
  const [depPublicId, setDepPublicId] = useState<string>('');

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const { isLoading, mutate: restoreDependent } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    {}
  >({
    queryKey: RESTORE_DEPENDENT_QUERY_KEY,
    queryKeysToInvalidate: `${GET_DEPENDENTS_QUERY_KEY}-${enrollmentContextType}`,
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/dependents/profile/${dependentId}/reactivate/?enrollment_type_lookup_code=${enrollmentContextType}&public_id=${depPublicId}&life_event_completed=false`,
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const handleRestore = (e: any) => {
    const elementId = e?.currentTarget?.id.split('_')[0];
    const depId = elementId.split('-')[1] ? elementId.split('-')[1] : '';
    const publicId = elementId.split('-')[2] ? elementId.split('-')[2] : '';
    setDependentId(depId);
    setDepPublicId(publicId);
  };

  useEffect(() => {
    document
      .querySelectorAll('button[id^="restoreDependent"][id$="_primary"]')
      .forEach((modalSaveBtn) => {
        modalSaveBtn.addEventListener('click', (e: Event) => handleRestore(e));
      });
  }, []);

  useEffect(() => {
    if (dependentId !== '' && depPublicId !== '') {
      restoreDependent({});
    }
  }, [dependentId, depPublicId, restoreDependent]);

  return (
    <>
      {isLoading ? (
        <div className="mt-5">
          <Loader />
        </div>
      ) : (
        <Grid
          container
          spacing={4}
          className="my-5"
          data-testid="enrollment-removed-family-member"
          tabIndex={0}
          id="removed-family-members"
        >
          {data?.map((dependent: any) => (
            <FamilyMemberCard
              dependent={dependent}
              key={dependent.id}
              dependentsData={dependentsData}
              showViewDetails={true}
            >
              <div className="mt-3">
                <ps-button
                  id={`restoreDependent-${dependent.id}-btn`}
                  label={t('restoreButtonLabel')}
                  icon="plus"
                  level="tertiary"
                  other-data={`${`data-bs-toggle='modal' data-bs-target='#restoreDependent-${dependent.id}-${dependent.public_id}'`}`}
                ></ps-button>
                <PSModalWrapper
                  id={`restoreDependent-${dependent.id}-${dependent.public_id}`}
                  message={t('restoreDependentModalMessage', {
                    dependent_name: capitalize(dependent.first_name),
                  })}
                  title={t('restoreDependentModalTitle', {
                    dependent_name: capitalize(dependent.first_name),
                  })}
                  primaryButton={t('confirmButtonLabel')}
                  primaryActive={true}
                  secondaryButton={t('cancelButtonLabel')}
                  extraClass="text-left"
                ></PSModalWrapper>
              </div>
            </FamilyMemberCard>
          ))}
        </Grid>
      )}
    </>
  );
};
